import styled from "@emotion/styled";
import { BoughtOrSoldFields, TokenHistoryInfo, TransactionDetail } from "types/history";
import { Text } from "../../../general/Text";
import { GlobalStore } from "store/global/GlobalStore";
import { getTokenIcon } from "utils/tokenIconUtils";
import { FlexBetweenContainer } from "containers/FlexBetweenContainer";
import { Spacer } from "components/general/Spacer/Spacer";
import { PropsWithChildren } from "react";
import { Hash } from "viem";
import { getTokenTicker } from "../../../../utils/tokenUtils";

interface ActivityDetailBlockProps {
  sellTokens: TransactionDetail["sellTokens"];
  buyTokens: TransactionDetail["buyTokens"];
  isTradeAndSend: boolean;
  chainId: TransactionDetail["chain_id"];
}

export const ActivityDetailTradeLegs = ({
  sellTokens,
  buyTokens,
  isTradeAndSend,
  chainId,
}: ActivityDetailBlockProps) => {
  return (
    <ActivityDetailTradeLegsContainer>
      <Leg title="You Paid">
        {Object.entries(sellTokens).map(([tokenAddress, txDetail]) => (
          <TokenRow
            {...{
              chainId: chainId,
              tokenAddress: tokenAddress as Hash,
              amount: txDetail.amount,
              amountUsd: txDetail.amountUsd,
            }}
            key={tokenAddress}
          />
        ))}
      </Leg>
      <Spacer height="8px" />
      <Leg title={`You ${isTradeAndSend ? "Sent" : "Received"} Exactly`}>
        {Object.entries(buyTokens).map(([tokenAddress, txDetail]) => (
          <TokenRow
            {...{
              chainId: chainId,
              tokenAddress: tokenAddress as Hash,
              amount: txDetail.amount,
              amountUsd: txDetail.amountUsd,
            }}
            key={tokenAddress}
          />
        ))}
      </Leg>
    </ActivityDetailTradeLegsContainer>
  );
};

// Sub-components

const ActivityDetailTradeLegsContainer = styled.div`
  margin-bottom: 15px;
`;

const LegContainer = styled.div`
  display: flex;
  padding: 16px 16px 0px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-radius: 14px;
  background: ${props => props.theme.surface.alt.secondary.hover};
`;

const Leg = ({ title, children }: PropsWithChildren<{ title: string }>) => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <LegContainer>
      <Text textStyle="Title" size="XS" color={theme.font.secondary.default}>
        {title}
      </Text>
      {children}
    </LegContainer>
  );
};

const TokenRowWrapper = styled.div`
  width: 100%;
`;

const TokenRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 10px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const UsdRowContainer = styled.div`
  width: 100%;
  text-align: end;
  position: relative;
  top: -15px;
`;

interface TokenRowProps extends Omit<BoughtOrSoldFields, "address"> {
  tokenAddress: keyof TokenHistoryInfo;
  chainId: TransactionDetail["chain_id"];
}

const TokenRow = ({ tokenAddress, amount, amountUsd, chainId }: TokenRowProps) => {
  const theme = GlobalStore.useState(s => s.theme);
  const tokenLists = GlobalStore.useState(s => s.tokenLists);
  const tokenList = tokenLists ? tokenLists[chainId] : undefined;
  return (
    <TokenRowWrapper>
      <TokenRowContainer>
        <FlexBetweenContainer>
          <img
            src={getTokenIcon(tokenAddress, tokenList)}
            alt={tokenAddress}
            width={30}
            height={30}
          />
          <Spacer width="8px" />
          <Text textStyle="Paragraph" size="XL">
            {getTokenTicker(tokenAddress, tokenList)}
          </Text>
        </FlexBetweenContainer>
        <div>
          <Text textStyle="Title" size="Small">
            {amount.toDisplay(tokenAddress, tokenList)} {getTokenTicker(tokenAddress, tokenList)}
          </Text>
        </div>
      </TokenRowContainer>

      <UsdRowContainer>
        <Text textStyle="Paragraph" size="Large" color={theme.font.tertiary.default}>
          ${amountUsd.toFixed(2)}
        </Text>
      </UsdRowContainer>
    </TokenRowWrapper>
  );
};
